// OpenAI API 服務
export interface OpenAIConfig {
  apiKey: string;
  model: string;
  maxTokens: number;
  temperature: number;
}

export interface OpenAIResponse {
  policyName: string;
  companyName: string;
  annualPremium?: number; // 年繳保費（數字）
  monthlyPremium: number; // 月繳保費（數字）
  premiumPaymentType?: 'annual' | 'monthly' | 'one-time'; // 保費繳納方式
  policyType: string;
  policyNumber: string;
  effectiveDate: string;
  expiryDate?: string; // 到期日期（YYYY-MM-DD格式，西元年）
  coverageAmount: string;
  confidence: number;
}

class OpenAIService {
  private config: OpenAIConfig;

  constructor() {
    this.config = {
      apiKey: process.env.REACT_APP_OPENAI_API_KEY || '',
      // gpt-3.5-turbo 的 context window 太小，長保單整份文字一塞進去就爆掉，
      // 導致分析失敗、回退成準確度很低的關鍵字猜測（保單名稱/保費因此抓錯）。
      // 換成 gpt-4o-mini：一次能處理的文字量大很多，足夠涵蓋完整保單，
      // 費用同時比 gpt-3.5-turbo 便宜、準確度也更好，不需要再對輸入文字做裁切。
      model: process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini',
      maxTokens: parseInt(process.env.REACT_APP_OPENAI_MAX_TOKENS || '1000'),
      temperature: parseFloat(process.env.REACT_APP_OPENAI_TEMPERATURE || '0')
    };
  }

  // 檢查API配置是否完整
  isConfigured(): boolean {
    return !!this.config.apiKey && this.config.apiKey !== 'your_openai_api_key_here';
  }

  // 分析保單文本
  async analyzePolicyText(text: string): Promise<OpenAIResponse> {
    if (!this.isConfigured()) {
      throw new Error('OpenAI API未配置，請設置REACT_APP_OPENAI_API_KEY環境變量');
    }

    try {
      console.log('開始使用OpenAI分析保單文本...');
      
      const prompt = this.createAnalysisPrompt(text);
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          model: this.config.model,
          messages: [
            {
              role: 'system',
              content: '你是一個專業的保險保單分析師，擅長從保單文本中提取關鍵信息。'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: this.config.maxTokens,
          temperature: this.config.temperature
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API錯誤: ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;
      
      if (!content) {
        throw new Error('OpenAI API返回空內容');
      }

      // 解析JSON響應
      console.log('OpenAI原始響應內容:', content);
      const analysisResult = this.parseAnalysisResult(content);
      
      console.log('OpenAI分析完成:', analysisResult);
      return analysisResult;

    } catch (error: any) {
      console.error('OpenAI分析失敗:', error);
      throw new Error(`OpenAI分析失敗: ${error.message}`);
    }
  }

  // 創建分析提示詞
  private createAnalysisPrompt(text: string): string {
    // 不同保單排版差異很大，摘要資訊（保單名稱/保費/日期）不一定都在前幾頁，
    // 所以不對輸入文字做「賭內容在前面」式的裁切，而是靠 analyzePolicyText()
    // 裡改用的大 context window 模型（gpt-4o-mini）直接處理完整文件。
    // 這裡只保留一個很寬鬆的保護上限，防止極端情況（例如 OCR 誤判掃出
    // 大量重複亂碼）把 prompt 撐到不合理的大小。
    const SAFETY_LIMIT = 100000;
    const safeText = text.length > SAFETY_LIMIT ? text.substring(0, SAFETY_LIMIT) : text;
    if (text.length > SAFETY_LIMIT) {
      console.warn(`⚠️ 保單文本長度 ${text.length} 超過安全上限 ${SAFETY_LIMIT}，可能是擷取內容異常（如 OCR 亂碼），僅取前 ${SAFETY_LIMIT} 字分析`);
    }

    return `
你是一個專業的保險保單分析師。請仔細分析以下保險保單文本，提取關鍵信息。

保單文本內容：
${safeText}

請以JSON格式返回以下信息，確保準確性和一致性：

{
  "policyName": "具體的保單名稱（如：富邦人壽終身壽險、國泰健康保險等）",
  "companyName": "完整的保險公司名稱（如：富邦人壽、國泰人壽等）",
  "annualPremium": "年繳保費金額（數字，不含貨幣符號，如：5580）",
  "monthlyPremium": "月繳保費金額（數字，不含貨幣符號，如：465）",
  "premiumPaymentType": "保費繳納方式（'annual'年繳、'monthly'月繳、'one-time'一次性）",
  "policyType": "保險類型（如：人壽保險、健康保險、意外保險、旅行保險等）",
  "policyNumber": "保單號碼",
  "effectiveDate": "生效日期（YYYY-MM-DD格式，西元年）",
  "expiryDate": "到期日期（YYYY-MM-DD格式，西元年，查找'滿期日'、'到期日'、'失效日期'等）",
  "coverageAmount": "保障金額（如：NT$200,000）"
}

重要分析要求：

【日期提取規則】
1. 日期格式轉換：
   - 如果文本中是「民國111年09月22日」→ 轉換為 "2022-09-22"（民國年+1911=西元年）
   - 如果文本中是「民國170年09月22日」→ 轉換為 "2081-09-22"（民國年+1911=西元年）
   - 民國年轉西元年公式：民國年 + 1911 = 西元年
   - 支持格式：「民國XXX年XX月XX日」、「XXX年XX月XX日」、「XXX/XX/XX」（需判斷是否為民國年）
2. 生效日期查找關鍵字：「生效日」、「生效日期」、「申請日」
3. 到期日期查找關鍵字：「滿期日」、「到期日」、「失效日期」、「終止日期」、「續保終日」

【保費提取規則】
1. 首先查找「繳別」欄位，確定是「年繳」、「月繳」還是「一次性」
2. 年繳保單：
   - 查找「應繳保險費合計」、「標準保險費」、「年繳保費」等關鍵字
   - annualPremium = 提取的年繳金額（純數字，不含NT$、逗號等）
   - monthlyPremium = annualPremium ÷ 12（四捨五入到整數）
   - premiumPaymentType = "annual"
3. 月繳保單：
   - 查找「月繳保費」、「每月保費」等關鍵字
   - monthlyPremium = 提取的月繳金額（純數字）
   - annualPremium = monthlyPremium × 12
   - premiumPaymentType = "monthly"
4. 一次性保單（旅行保險、短期保險等）：
   - 直接提取總保費金額
   - annualPremium = 總保費金額
   - monthlyPremium = 0 或與 annualPremium 相同
   - premiumPaymentType = "one-time"

【其他要求】
1. 保單名稱要具體明確，避免使用"綜合保險保單"等通用名稱
2. 保險公司名稱要完整，包含"人壽"、"保險"等後綴
3. 如果某項信息在文本中找不到，請標記為"無法確定"或返回空字符串
4. 優先從文本中提取真實信息，不要猜測
5. 確保分析結果的一致性，相同保單應產生相同結果
6. 數字字段（annualPremium, monthlyPremium）只返回純數字，不要包含貨幣符號、逗號等

請只返回JSON格式的結果，不要包含其他文字。
`;
  }

  // 解析分析結果
  private parseAnalysisResult(content: string): OpenAIResponse {
    try {
      console.log('開始解析OpenAI響應...');
      console.log('響應內容長度:', content.length);
      console.log('響應內容預覽:', content.substring(0, 200));
      
      // 清理響應內容，移除可能的代碼塊標記
      let cleanedContent = content.trim();
      if (cleanedContent.startsWith('```json')) {
        cleanedContent = cleanedContent.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (cleanedContent.startsWith('```')) {
        cleanedContent = cleanedContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }
      
      // 嘗試解析JSON
      const result = JSON.parse(cleanedContent);
      console.log('JSON解析成功:', result);
      
      // 處理保費數字（移除貨幣符號、逗號等，轉換為數字）
      const parsePremium = (value: any): number => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          // 移除貨幣符號、逗號、空格等
          const cleaned = value.replace(/[NT$,\s]/g, '');
          const num = parseFloat(cleaned);
          return isNaN(num) ? 0 : Math.round(num);
        }
        return 0;
      };

      // 驗證必要字段
      const analysisResult: OpenAIResponse = {
        policyName: result.policyName || '無法確定',
        companyName: result.companyName || '無法確定',
        annualPremium: result.annualPremium !== undefined ? parsePremium(result.annualPremium) : undefined,
        monthlyPremium: parsePremium(result.monthlyPremium || 0),
        premiumPaymentType: result.premiumPaymentType || undefined,
        policyType: result.policyType || '綜合保險',
        policyNumber: result.policyNumber || 'N/A',
        effectiveDate: result.effectiveDate || new Date().toISOString().split('T')[0],
        expiryDate: result.expiryDate || undefined,
        coverageAmount: result.coverageAmount || '無法確定',
        confidence: this.calculateConfidence(result)
      };

      console.log('最終分析結果:', analysisResult);
      return analysisResult;

    } catch (error) {
      console.warn('無法解析OpenAI響應，使用備用解析:', error);
      
      // 備用解析：嘗試從文本中提取JSON
      // 首先嘗試移除```json```包裝
      let cleanContent = content.replace(/```json\s*|\s*```/g, '').trim();
      
      try {
        const result = JSON.parse(cleanContent);
        console.log('備用解析成功:', result);
        
        // 處理保費數字
        const parsePremium = (value: any): number => {
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const cleaned = value.replace(/[NT$,\s]/g, '');
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : Math.round(num);
          }
          return 0;
        };
        
        // 直接返回結果，不需要再次調用parseAnalysisResult
        const analysisResult: OpenAIResponse = {
          policyName: result.policyName || '無法確定',
          companyName: result.companyName || '無法確定',
          annualPremium: result.annualPremium !== undefined ? parsePremium(result.annualPremium) : undefined,
          monthlyPremium: parsePremium(result.monthlyPremium || 0),
          premiumPaymentType: result.premiumPaymentType || undefined,
          policyType: result.policyType || '綜合保險',
          policyNumber: result.policyNumber || 'N/A',
          effectiveDate: result.effectiveDate || new Date().toISOString().split('T')[0],
          expiryDate: result.expiryDate || undefined,
          coverageAmount: result.coverageAmount || '無法確定',
          confidence: this.calculateConfidence(result)
        };
        
        console.log('最終分析結果:', analysisResult);
        return analysisResult;
      } catch (e) {
        // 如果還是失敗，嘗試提取JSON對象
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const result = JSON.parse(jsonMatch[0]);
            console.log('JSON匹配解析成功:', result);
            
            const analysisResult: OpenAIResponse = {
              policyName: result.policyName || '無法確定',
              companyName: result.companyName || '無法確定',
              monthlyPremium: result.monthlyPremium || 'NT$0',
              policyType: result.policyType || '綜合保險',
              policyNumber: result.policyNumber || 'N/A',
              effectiveDate: result.effectiveDate || new Date().toISOString().split('T')[0],
              coverageAmount: result.coverageAmount || '無法確定',
              confidence: this.calculateConfidence(result)
            };
            
            console.log('最終分析結果:', analysisResult);
            return analysisResult;
          } catch (e2) {
            console.warn('JSON匹配解析也失敗:', e2);
          }
        }
      }
      
      // 返回默認分析結果
      return {
        policyName: '無法確定',
        companyName: '無法確定',
        monthlyPremium: 0, // 改為數字類型
        policyType: '綜合保險',
        policyNumber: 'N/A',
        effectiveDate: new Date().toISOString().split('T')[0],
        coverageAmount: '無法確定',
        confidence: 0.3
      };
    }
  }

  // 計算分析可信度
  private calculateConfidence(result: any): number {
    let confidence = 0.5; // 基礎分數
    
    // 根據提取到的信息質量調整可信度
    if (result.policyName && result.policyName !== '無法確定') confidence += 0.1;
    if (result.companyName && result.companyName !== '無法確定') confidence += 0.1;
    if (result.monthlyPremium && result.monthlyPremium !== 'NT$0') confidence += 0.1;
    if (result.policyType && result.policyType !== '綜合保險') confidence += 0.1;
    if (result.policyNumber && result.policyNumber !== 'N/A') confidence += 0.1;
    
    return Math.min(confidence, 1.0);
  }

  // 獲取API使用統計
  getUsageStats(): { model: string; maxTokens: number; temperature: number } {
    return {
      model: this.config.model,
      maxTokens: this.config.maxTokens,
      temperature: this.config.temperature
    };
  }
}

export const openaiService = new OpenAIService();
